<template>
  <v-container
    v-if="images.length"
    class="mb-4">
    <v-carousel
      height="400"
      hide-delimiter-background
      :model-value="images.findIndex((item) => item.uuid === activeImage)"
      show-arrows="hover"
      @update:model-value="
        (e) => {
          activeImage = images[e].uuid;
        }
      ">
      <v-carousel-item
        v-for="item in images"
        :key="item.uuid"
        cover
        :src="item.url" />
    </v-carousel>
  </v-container>

  <v-container
    v-if="attachments.length && !hideDownloadLink"
    class="mb-4">
    <a
      ref="downloadLink"
      href="#"
      style="display: none"
      target="_blank" />
    <v-chip
      v-for="item in attachments"
      :key="item.uuid"
      class="ma-2"
      :closable="!readOnly"
      :color="item.uuid === activeImage ? 'blue' : 'darkgrey'"
      prepend-icon="mdi-download"
      @click="
        () => {
          downloadLink.href = item.url;
          downloadLink.setAttribute('download', item.filename);
          downloadLink.click();
        }
      "
      @click:close="onDelete(item.uuid)">
      {{ item.filename }}
    </v-chip>
  </v-container>

  <v-sheet
    v-if="!readOnly"
    border="lg"
    class="mx-auto text-center px-8 mb-4 border-dashed d-flex"
    :class="isDragActive ? 'bg-lime-lighten-3' : 'bg-lime-lighten-4'"
    height="100"
    rounded="lg"
    v-bind="getRootProps()">
    <input v-bind="getInputProps()" />
    <p
      v-if="isDragActive"
      class="align-self-center">
      {{ t("components.CarouselWithUpload.DropHere") }}
    </p>
    <p
      v-else
      class="align-self-center">
      {{ t("components.CarouselWithUpload.DragOrSelect") }}
    </p>
  </v-sheet>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useTranslation } from "i18next-vue";
import axios from "axios";
import { useDropzone } from "vue3-dropzone";
import { useSnackbar } from "vuetify-use-dialog";
import { useSpaceStore } from "@/stores/spaceStore";
import { useMainStore } from "@/stores/mainStore";
import { uuid4 } from "@sentry/core";

const mainStore = useMainStore();

defineExpose({
  uploadAcceptedFiles
});

const { t } = useTranslation();
const createSnackbar = useSnackbar();

const attachments = ref([]);
const images = ref([]);
const downloadLink = ref(null);
const activeImage = ref(null);

const props = defineProps({
  nodeId: {
    type: String,
    required: true
  },
  field: {
    type: String,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  uploadLater: {
    type: Boolean,
    default: false
  },
  spaceId: {
    type: String,
    default: undefined
  },
  hideDownloadLink: {
    type: Boolean,
    default: false
  }
});

const spaceId = computed(() => props.spaceId || mainStore.activeSpaceId);

const spaceStore = useSpaceStore();

const acceptedFiles = ref([]);

function uploadAcceptedFiles(overwriteId) {
  console.log("uploadAcceptedFiles", acceptedFiles.value);
  const formData = new FormData(); // pass data as a form
  for (let x = 0; x < acceptedFiles.value.length; x++) {
    formData.append("attachments", acceptedFiles.value[x]);
  }

  axios
    .post(
      `/api/attachments/${spaceId.value}/${overwriteId}/${props.field}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    )
    .then((response) => {
      attachments.value = attachments.value.concat(response.data);
      images.value = images.value.concat(
        response.data.filter((item) => item.filename.split(".").pop() !== "pdf")
      );
      // console.info("new images", images.value);
    })
    .catch((err) => {
      console.error(err);
    });
}

const onDropAccepted = (acceptFiles) => {
  console.log("Accepted files", acceptFiles);

  if (props.uploadLater) {
    const oldLength = acceptedFiles.value.length;
    console.log("oldLength", oldLength);
    acceptedFiles.value = acceptedFiles.value.concat(acceptFiles);
    console.log("oldLength", oldLength);

    const reader = new FileReader();
    function readFile(index) {
      console.log("File", index, acceptedFiles.value[index]);
      if (index >= acceptedFiles.value.length) return;
      var file = acceptedFiles.value[index];
      reader.onload = function (e) {
        // get file content
        images.value.push({
          filename: file.name,
          url: e.target.result,
          uuid: index
        });
        attachments.value.push({
          filename: file.name,
          url: e.target.result,
          uuid: index
        });
        // do sth with bin
        readFile(index + 1);
      };
      reader.readAsDataURL(acceptedFiles.value[index]);
    }
    readFile(oldLength);

    return;
  }

  const formData = new FormData(); // pass data as a form
  for (let x = 0; x < acceptFiles.length; x++) {
    formData.append("attachments", acceptFiles[x]);
  }

  axios
    .post(
      `/api/attachments/${spaceId.value}/${props.nodeId}/${props.field}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    )
    .then((response) => {
      attachments.value = attachments.value.concat(response.data);
      images.value = images.value.concat(
        response.data.filter((item) => item.filename.split(".").pop() !== "pdf")
      );
      // console.info("new images", images.value);
    })
    .catch((err) => {
      console.error(err);
    });
};

const onDropRejected = (rejectedFiles) => {
  rejectedFiles.forEach(({ file, errors }) => {
    createSnackbar({
      text:
        `${file.name}: ` +
        t(`components.CarouselWithUpload.errors.${errors[0].code}`),
      showCloseButton: false,
      snackbarProps: { color: "error", timeout: 3000 },
      multiline: true
    });
  });
};

const { getRootProps, getInputProps, isDragActive } = useDropzone({
  onDropAccepted,
  multiple: true,
  accept: ["image/*", ".pdf"],
  onDropRejected
});

const loadAttachments = () => {
  console.log(
    "loadAttachments",
    spaceId.value,
    props.nodeId,
    props.field,
    `/api/attachments/${spaceId.value}/${props.nodeId}/${props.field}`
  );
  axios
    .get(`/api/attachments/${spaceId.value}/${props.nodeId}/${props.field}`)
    .then((response) => {
      attachments.value = response.data;
      images.value = response.data.filter(
        (item) => item.filename.split(".").pop() !== "pdf"
      );
    })
    .catch((err) => {
      console.error(err);
    });
};

onMounted(() => {
  loadAttachments();
});

watch(
  () => `${props.nodeId}|${props.field}`,
  (nodes) => {
    loadAttachments();
  }
);

const onDelete = (uuid) => {
  if (props.uploadLater) {
    attachments.value = attachments.value.filter((item) => item.uuid !== uuid);
    images.value = images.value.filter((item) => item.uuid !== uuid);
    acceptedFiles.value.splice(uuid, 1);
    return;
  }

  axios
    .delete(
      `/api/attachments/${spaceId.value}/${props.nodeId}/${props.field}/${uuid}`
    )
    .then(() => {
      attachments.value = attachments.value.filter(
        (item) => item.uuid !== uuid
      );
      images.value = images.value.filter((item) => item.uuid !== uuid);
    })
    .catch((err) => {
      console.error(err);
    });
};
</script>
